import React, { useState, useEffect } from 'react';
import { Music2, SkipBack, SkipForward, Play, Pause, Volume2, Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Song {
  title: string;
  artist: string;
  bpm: number;
  duration: number;
  liked: boolean;
  albumArt?: string;
}

export const CurrentSong = () => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentSong, setCurrentSong] = useState<Song>({
    title: "Running Up That Hill",
    artist: "Kate Bush",
    bpm: 170,
    duration: 285, // in seconds
    liked: false,
    albumArt: "https://images.unsplash.com/photo-1611339555312-e607c8352fd7?w=400&h=400&fit=crop"
  });

  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        setProgress(prev => (prev + 1) % currentSong.duration);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isPlaying, currentSong.duration]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => setIsPlaying(!isPlaying);
  const toggleLike = () => {
    setCurrentSong(prev => ({ ...prev, liked: !prev.liked }));
  };

  return (
    <motion.div 
      className="glass p-6 rounded-xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex gap-4">
        <motion.div 
          className="relative w-16 h-16 rounded-lg overflow-hidden glass"
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          {currentSong.albumArt ? (
            <img 
              src={currentSong.albumArt} 
              alt={currentSong.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Music2 className="w-8 h-8" />
            </div>
          )}
          <motion.div 
            className="absolute inset-0 bg-black/40 flex items-center justify-center"
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
          >
            <button 
              onClick={togglePlay}
              className="p-2 glass rounded-full hover:bg-white/20 transition-colors"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
          </motion.div>
        </motion.div>

        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="font-semibold text-lg leading-tight">{currentSong.title}</h3>
              <p className="text-sm text-white/70">{currentSong.artist}</p>
            </div>
            <button 
              onClick={toggleLike}
              className={`p-2 glass rounded-full transition-colors ${
                currentSong.liked ? 'text-red-400 hover:bg-red-500/20' : 'hover:bg-white/20'
              }`}
            >
              <Heart className="w-4 h-4" fill={currentSong.liked ? "currentColor" : "none"} />
            </button>
          </div>

          <div className="space-y-2">
            <div className="relative h-1 bg-white/10 rounded-full overflow-hidden">
              <motion.div 
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-400 to-purple-400"
                style={{ width: `${(progress / currentSong.duration) * 100}%` }}
              />
            </div>
            <div className="flex justify-between items-center text-xs text-white/50">
              <span>{formatTime(progress)}</span>
              <span>{formatTime(currentSong.duration)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Volume2 className="w-4 h-4 text-white/70" />
          <div className="text-xs">
            <span className="font-medium">{currentSong.bpm} BPM</span>
            <span className="text-white/50 ml-1">Matched to pace</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="p-2 glass rounded-full hover:bg-white/20 transition-colors">
            <SkipBack className="w-4 h-4" />
          </button>
          <button className="p-2 glass rounded-full hover:bg-white/20 transition-colors">
            <SkipForward className="w-4 h-4" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isPlaying && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="mt-4 glass p-3 rounded-lg"
          >
            <p className="text-sm text-white/70">
              <span className="font-medium text-white/90">Pro Tip:</span> This song's tempo perfectly matches your current running cadence for optimal performance.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};